import { type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';
import { breakpoint } from '../theme/breakpoints.stylex';

const styles = stylex.create({
  root: {
    display: 'grid',
    gap: '1.5rem',
    maxWidth: '1180px',
    width: '100%',
    paddingTop: { default: '2rem', [breakpoint.narrow]: '1.25rem' },
    paddingBottom: { default: '2rem', [breakpoint.narrow]: '1.25rem' },
    paddingLeft: { default: '2rem', [breakpoint.narrow]: '1rem' },
    paddingRight: { default: '2rem', [breakpoint.narrow]: '1rem' },
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1.5rem',
    flexWrap: 'wrap',
  },
  heading: { display: 'grid', gap: '0.375rem', minWidth: 0 },
  title: {
    margin: 0,
    fontFamily: sentinel.fontDisplay,
    fontSize: { default: '1.875rem', [breakpoint.narrow]: '1.5rem' },
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: sentinel.textBody,
    lineHeight: 1.2,
    overflowWrap: { default: 'normal', [breakpoint.narrow]: 'anywhere' },
  },
  subtitle: {
    overflowWrap: { default: 'normal', [breakpoint.narrow]: 'anywhere' },
    margin: 0,
    fontFamily: sentinel.fontBody,
    fontSize: '0.9375rem',
    color: sentinel.textMuted,
    lineHeight: 1.5,
    maxWidth: '60ch',
  },
  actions: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  body: { display: 'grid', gap: '1.25rem' },
});

export interface PageLayoutProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Sets `data-testid` on the <h1>; e2e suites use this as a stable hook. */
  titleTestId?: string;
  /** Sets `data-testid` on the actions wrapper. */
  actionsTestId?: string;
}

export function PageLayout({
  title,
  subtitle,
  actions,
  children,
  titleTestId,
  actionsTestId,
}: PageLayoutProps) {
  return (
    <section {...stylex.props(styles.root)}>
      <header {...stylex.props(styles.header)}>
        <div {...stylex.props(styles.heading)}>
          <h1 {...stylex.props(styles.title)} data-testid={titleTestId}>
            {title}
          </h1>
          {subtitle ? <p {...stylex.props(styles.subtitle)}>{subtitle}</p> : null}
        </div>
        {actions ? (
          <div {...stylex.props(styles.actions)} data-testid={actionsTestId}>
            {actions}
          </div>
        ) : null}
      </header>
      <div {...stylex.props(styles.body)}>{children}</div>
    </section>
  );
}
